import type { FormInstance } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import type { CrmScopedViewMode } from '@/modules/zy-crm/utils/crm-view';
import { enrichCustomerCompany } from '@/services/bff/customer.api';
import type {
  CompanyEnrichmentField,
  CompanyEnrichmentResult,
} from '@/services/bff/types/customer';
import type { CustomerFormValues } from './payload';

const IDENTIFIERS = ['name', 'fullName', 'unifiedSocialCreditCode'] as const;
const FIELDS: CompanyEnrichmentField[] = [
  'fullName',
  'unifiedSocialCreditCode',
  'legalRepresentative',
  'registeredCapital',
  'establishmentDate',
  'website',
  'remark',
  'telephone',
  'email',
  'qichachaUrl',
  'tianyanchaUrl',
];
export const enrichmentValue = (value: unknown): string =>
  dayjs.isDayjs(value)
    ? value.format('YYYY-MM-DD')
    : String(value ?? '').trim();
const validUrl = (value: string, domain?: string) => {
  try {
    const url = new URL(value);
    return (
      ['https:', 'http:'].includes(url.protocol) &&
      !url.username &&
      !url.password &&
      (!domain ||
        ((!url.port || ['80', '443'].includes(url.port)) &&
          (url.hostname === domain || url.hostname.endsWith(`.${domain}`))))
    );
  } catch {
    return false;
  }
};
const LIMITS: Record<CompanyEnrichmentField, number> = {
  fullName: 255,
  unifiedSocialCreditCode: 18,
  legalRepresentative: 100,
  registeredCapital: 100,
  establishmentDate: 10,
  website: 255,
  remark: 500,
  telephone: 100,
  email: 255,
  qichachaUrl: 1000,
  tianyanchaUrl: 1000,
};
const validSuggestion = (field: CompanyEnrichmentField, value: string) => {
  if (!value || value.length > LIMITS[field]) return false;
  if (field === 'establishmentDate')
    return (
      /^\d{4}-\d{2}-\d{2}$/.test(value) &&
      dayjs(value).isValid() &&
      dayjs(value).format('YYYY-MM-DD') === value
    );
  if (field === 'unifiedSocialCreditCode')
    return /^[0-9A-HJ-NPQRTUWXY]{18}$/i.test(value);
  if (field === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  if (field === 'website') return validUrl(value);
  if (field === 'qichachaUrl') return validUrl(value, 'qcc.com');
  if (field === 'tianyanchaUrl') return validUrl(value, 'tianyancha.com');
  return true;
};
const identifiers = (form: FormInstance<CustomerFormValues>) =>
  Object.fromEntries(
    IDENTIFIERS.map((key) => [
      key,
      String(form.getFieldValue(key) || '').trim(),
    ]),
  ) as Record<(typeof IDENTIFIERS)[number], string>;

export type CompanyEnrichmentSuggestion = {
  field: CompanyEnrichmentField;
  value: string;
  currentValue: string;
  selected: boolean;
  source: { url: string; title: string };
};
export type CompanyEnrichmentFeedback = {
  suggestions?: CompanyEnrichmentSuggestion[];
  count: number;
  result?: CompanyEnrichmentResult;
  error?: string;
};

export function useCompanyEnrichment({
  form,
  open,
  recordUuid,
  viewMode,
}: {
  form: FormInstance<CustomerFormValues>;
  open: boolean;
  recordUuid?: string;
  viewMode: CrmScopedViewMode;
}) {
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<CompanyEnrichmentFeedback>();
  const feedbackRef = useRef<CompanyEnrichmentFeedback | undefined>(undefined);
  const reviewRef = useRef<
    { scope: string; query: ReturnType<typeof identifiers> } | undefined
  >(undefined);
  const updateFeedback = (next?: CompanyEnrichmentFeedback) => {
    feedbackRef.current = next;
    setFeedback(next);
  };
  const requestRef = useRef<
    { controller: AbortController; version: number } | undefined
  >(undefined);
  const version = useRef(0);
  const scope = `${open}:${recordUuid || ''}:${viewMode}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const invalidate = () => {
    version.current += 1;
    requestRef.current?.controller.abort();
    requestRef.current = undefined;
    setLoading(false);
    updateFeedback(undefined);
    reviewRef.current = undefined;
  };
  useEffect(() => {
    invalidate();
    return () => {
      version.current += 1;
      requestRef.current?.controller.abort();
      requestRef.current = undefined;
    };
  }, [scope]);

  const supplement = async () => {
    if (!open || requestRef.current) return;
    const query = identifiers(form);
    if (!Object.values(query).some(Boolean)) {
      updateFeedback({
        count: 0,
        error: '请先填写信用代码、企业全称或客户名称',
      });
      return;
    }
    if (
      query.unifiedSocialCreditCode &&
      !/^[0-9A-HJ-NPQRTUWXY]{18}$/i.test(query.unifiedSocialCreditCode)
    ) {
      updateFeedback({ count: 0, error: '请输入正确的18位统一社会信用代码' });
      return;
    }
    const request = {
      controller: new AbortController(),
      version: ++version.current,
    };
    requestRef.current = request;
    updateFeedback(undefined);
    setLoading(true);
    const current = () =>
      request.version === version.current &&
      scopeRef.current === scope &&
      JSON.stringify(query) === JSON.stringify(identifiers(form));
    try {
      const result = await enrichCustomerCompany(
        {
          ...Object.fromEntries(
            Object.entries(query).filter(([, value]) => value),
          ),
          recordUuid,
          viewMode,
        },
        request.controller.signal,
      );
      if (!current()) return;
      const suggestions: CompanyEnrichmentSuggestion[] = [];
      if (result.status === 'matched') {
        for (const field of FIELDS) {
          const value = result.fields[field]?.trim();
          const source = result.sources[field];
          const currentValue = enrichmentValue(form.getFieldValue(field));
          if (
            value &&
            source &&
            validUrl(source.url) &&
            validSuggestion(field, value) &&
            value !== currentValue
          ) {
            suggestions.push({
              field,
              value,
              source,
              currentValue,
              selected: true,
            });
          }
        }
      }
      reviewRef.current = { scope, query };
      updateFeedback({ count: 0, result, suggestions });
    } catch (error) {
      if (!current() || request.controller.signal.aborted) return;
      const err = error as {
        data?: { message?: string };
        info?: { message?: string };
        response?: { data?: { message?: string | string[]; msg?: string } };
        message?: string;
      };
      const reason =
        err.response?.data?.message ||
        err.response?.data?.msg ||
        err.data?.message ||
        err.info?.message ||
        err.message ||
        '企业信息补全失败，请稍后重试';
      updateFeedback({
        count: 0,
        error: Array.isArray(reason) ? reason.join('，') : reason,
      });
    } finally {
      if (requestRef.current === request) {
        requestRef.current = undefined;
        setLoading(false);
      }
    }
  };
  const changeSelection = (
    fields: CompanyEnrichmentField[],
    selected: boolean,
  ) => {
    const feedback = feedbackRef.current;
    if (!feedback) return;
    updateFeedback({
      ...feedback,
      suggestions: feedback.suggestions?.map((item) =>
        fields.includes(item.field) ? { ...item, selected } : item,
      ),
    });
  };
  const confirm = (fields?: CompanyEnrichmentField[]) => {
    const feedback = feedbackRef.current;
    const review = reviewRef.current;
    if (!feedback || !review) return;
    if (
      review.scope !== scopeRef.current ||
      JSON.stringify(review.query) !== JSON.stringify(identifiers(form))
    ) {
      invalidate();
      return;
    }
    const patch: Partial<CustomerFormValues> = {};
    const remaining: CompanyEnrichmentSuggestion[] = [];
    let count = feedback.count;
    for (const item of feedback.suggestions || []) {
      const currentValue = enrichmentValue(form.getFieldValue(item.field));
      if (currentValue === item.value) continue;
      if (currentValue !== item.currentValue) {
        remaining.push({ ...item, currentValue, selected: false });
        continue;
      }
      if (fields ? fields.includes(item.field) : item.selected) {
        if (item.field === 'establishmentDate')
          patch.establishmentDate = dayjs(item.value);
        else patch[item.field] = item.value;
        count += 1;
      } else remaining.push(item);
    }
    if (Object.keys(patch).length) form.setFieldsValue(patch);
    reviewRef.current = { ...review, query: identifiers(form) };
    updateFeedback({ ...feedback, count, suggestions: remaining });
  };
  return {
    loading,
    feedback,
    supplement,
    invalidate,
    changeSelection,
    confirm,
    onValuesChange: (changed: Partial<CustomerFormValues>) => {
      if (IDENTIFIERS.some((key) => Object.hasOwn(changed, key))) {
        invalidate();
        return;
      }
      const feedback = feedbackRef.current;
      if (!feedback?.suggestions) return;
      updateFeedback({
        ...feedback,
        suggestions: feedback.suggestions.flatMap((item) => {
          if (!Object.hasOwn(changed, item.field)) return [item];
          const currentValue = enrichmentValue(form.getFieldValue(item.field));
          return currentValue === item.value
            ? []
            : [{ ...item, currentValue, selected: false }];
        }),
      });
    },
  };
}
